/**
 * StreamErrorNotice — renders a failed assistant turn.
 *
 * Before this existed, `status === 'error'` rendered nothing at all: the user
 * saw their own question, no reply, no explanation, and any already-streamed
 * partial text was thrown away. All 19 `assistant.error.*` i18n keys were
 * orphaned except the rate-limit one.
 *
 * Copy precedence:
 *   1. The server's `message_nb` when the error came from an SSE error event —
 *      that is curated Bokmål from the D-13 error matrix, and it is the only
 *      thing that can distinguish cases the client cannot tell apart (a spent
 *      household budget vs. a spent personal turn quota both arrive as
 *      `rate_limit`).
 *   2. Otherwise the i18n copy keyed by error code.
 *
 * Never renders `errorMessage` from a local failure path — those carry
 * technical strings ("HTTP 500", "Failed to fetch"). See StreamState.
 *
 * `rate_limit` is deliberately NOT handled here; it replaces the whole input
 * row via RateLimitExhaustedState. `aborted` is not an error — the user pressed
 * Stop.
 *
 * #158 (steg 8a): feiltilstanden er ikke tegnet («Not designed yet»), så den
 * restyles bare med tokenene, som en assistentboble i glass med lenkeknapper
 * på 44 px.
 *
 * data-testid: assistant-error-notice, assistant-error-retry
 */
import { useTranslation } from 'react-i18next';
import { Icon } from '../../components/primitives';
import { GLASS_SURFACE } from '../../components/primitives/Card';

/** Error codes the user can sensibly retry immediately. */
const RETRYABLE = new Set(['network', 'model_error', 'http_error', 'internal', 'generic']);
const START_OVER_CODES = new Set(['round_cap', 'token_budget']);

/** SSE/local error code → i18n key under `assistant.error`. */
const COPY_KEY: Record<string, string> = {
  network: 'network',
  model_error: 'api_unavailable',
  refusal: 'refused',
  round_cap: 'round_cap',
  token_budget: 'token_budget',
  misconfigured: 'misconfigured',
  food_block: 'food_block',
  // FF-13 (#181): ikke i RETRYABLE — et nytt forsøk hjelper ikke før
  // systemadminen slår AI på igjen eller en ny måned begynner.
  ai_disabled: 'ai_disabled',
  ai_budget: 'ai_budget',
  http_error: 'api',
};

export interface StreamErrorNoticeProps {
  code: string | undefined;
  /** The server's curated `message_nb`, if this error came from the server. */
  serverMessage?: string | undefined;
  fromServer?: boolean | undefined;
  /** Omit to hide the retry affordance (e.g. nothing to resend). */
  onRetry?: (() => void) | undefined;
  onStartNewConversation?: (() => void) | undefined;
}

export function StreamErrorNotice({
  code,
  serverMessage,
  fromServer,
  onRetry,
  onStartNewConversation,
}: StreamErrorNoticeProps) {
  const { t } = useTranslation();

  const fallbackKey = (code && COPY_KEY[code]) ?? 'generic';
  const text =
    fromServer && serverMessage ? serverMessage : t(`assistant.error.${fallbackKey}`);

  const canRetry = onRetry !== undefined && (!code || RETRYABLE.has(code));
  const canStartNewConversation =
    onStartNewConversation !== undefined &&
    !!code &&
    START_OVER_CODES.has(code);

  return (
    <div
      data-testid="assistant-error-notice"
      role="alert"
      className="flex justify-start my-2"
    >
      <div className={`${GLASS_SURFACE} mr-auto max-w-md rounded-row rounded-bl-xs px-4 py-3`}>
        <div className="flex items-start gap-2">
          <Icon.Bell size={18} className="mt-0.5 shrink-0 text-bad" />
          <div>
            <p className="text-base text-ink">{text}</p>
            {canRetry && (
              <button
                type="button"
                data-testid="assistant-error-retry"
                onClick={onRetry}
                className="mt-1 min-h-11 text-sm font-bold text-accent-text underline"
              >
                {t('assistant.error.retry')}
              </button>
            )}
            {canStartNewConversation && (
              <button
                type="button"
                data-testid="assistant-error-action"
                onClick={onStartNewConversation}
                className="mt-1 ml-3 min-h-11 text-sm font-bold text-accent-text underline"
              >
                {t('assistant.new_conversation')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
